import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import axios from 'axios';
import { ErrorContext } from '../context/ErrorContext';
import { configureAxiosAuth } from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';

export const AuthContext = createContext();

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARNING_AT_MS = 13 * 60 * 1000;
const PROACTIVE_REFRESH_WINDOW_MS = 60 * 1000;
const AUTH_BOOTSTRAP_REQUEST_TIMEOUT_MS = 10000;

const isTimeoutOrNetworkError = (error) => (
  error?.code === 'ECONNABORTED'
  || error?.message?.toLowerCase?.().includes('timeout')
  || !error?.response
);

export const AuthProvider = ({ children }) => {
  const { showError } = useContext(ErrorContext);
  const showErrorRef = useRef(showError);
  const accessTokenRef = useRef(null);
  const sessionMetaRef = useRef(null);
  const permissionsMapRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const lastInteractionRef = useRef(Date.now());
  const lastActivityUpdateRef = useRef(0);
  const authBootstrapRunRef = useRef(0);

  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionWarningVisible, setSessionWarningVisible] = useState(false);
  const [sessionWarningCountdownMs, setSessionWarningCountdownMs] = useState(0);
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);
  const [authInitializationError, setAuthInitializationError] = useState(null);
  const [tokenVersion, setTokenVersion] = useState(0);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const getPermissionsForRole = useCallback((role) => {
    const rolePermissions = permissionsMapRef.current || {};
    return rolePermissions?.[role]?.permissions || null;
  }, []);

  const setAnonymousState = useCallback(() => {
    accessTokenRef.current = null;
    sessionMetaRef.current = null;
    setTokenVersion((version) => version + 1);
    setUser(null);
    setPermissions(getPermissionsForRole('unauthenticated'));
    setSessionWarningVisible(false);
    setSessionWarningCountdownMs(0);
  }, [getPermissionsForRole]);

  const loadPermissionsMap = useCallback(async ({ timeoutMs } = {}) => {
    const response = await axios.get(`${API_URL}/permissions`, {
      withCredentials: true,
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
    });
    permissionsMapRef.current = response.data || {};
    return permissionsMapRef.current;
  }, []);

  const recordUserInteraction = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityUpdateRef.current < 750) return;
    lastActivityUpdateRef.current = now;
    lastInteractionRef.current = now;
  }, []);

  const applyAuthenticatedState = useCallback((payload) => {
    const nextUser = payload?.user || null;
    const nextAccessToken = payload?.accessToken || null;
    const nextSession = payload?.session || null;

    accessTokenRef.current = nextAccessToken;
    sessionMetaRef.current = nextSession;
    setTokenVersion((version) => version + 1);
    lastInteractionRef.current = Date.now();
    lastActivityUpdateRef.current = 0;

    setUser(nextUser);
    setPermissions(getPermissionsForRole(nextUser?.role));
    setAuthInitializationError(null);
    setSessionExpired(false);
    setSessionWarningVisible(false);
    setSessionWarningCountdownMs(0);
  }, [getPermissionsForRole]);

  const handleSessionExpiration = useCallback((message = 'Your session expired. Please log in again.') => {
    const hadSession = Boolean(user || accessTokenRef.current);
    setAnonymousState();
    if (!hadSession) return;
    setSessionExpired((alreadyExpired) => {
      if (!alreadyExpired) {
        showErrorRef.current?.(message);
      }
      return true;
    });
  }, [setAnonymousState, user]);

  const hasRecentInteraction = useCallback(() => {
    return Date.now() - lastInteractionRef.current < IDLE_TIMEOUT_MS;
  }, []);

  const refreshAccessToken = useCallback(async ({ force = false, timeoutMs } = {}) => {
    if (!force && !hasRecentInteraction()) {
      throw new Error('Session is idle and cannot be refreshed.');
    }

    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = axios
        .post(`${API_URL}/auth/refresh`, {}, {
          withCredentials: true,
          ...(timeoutMs ? { timeout: timeoutMs } : {}),
        })
        .then((response) => {
          applyAuthenticatedState(response.data);
          return response.data?.accessToken || null;
        })
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }

    return refreshPromiseRef.current;
  }, [applyAuthenticatedState, hasRecentInteraction]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // best effort only
    } finally {
      setAuthInitializationError(null);
      setSessionExpired(false);
      setAnonymousState();
    }
  }, [setAnonymousState]);

  useEffect(() => {
    configureAxiosAuth({
      getAccessTokenFn: () => accessTokenRef.current,
      shouldAttemptRefreshFn: () => Boolean(user && hasRecentInteraction()),
      refreshAccessTokenFn: () => refreshAccessToken({ force: false }),
      onAuthFailureFn: () => {
        if (user || accessTokenRef.current) {
          handleSessionExpiration();
        }
      },
    });
  }, [handleSessionExpiration, hasRecentInteraction, refreshAccessToken, user]);

  const bootstrapAuth = useCallback(async ({ showStartupErrors = false } = {}) => {
    const runId = authBootstrapRunRef.current + 1;
    authBootstrapRunRef.current = runId;
    setIsAuthInitializing(true);
    setAuthInitializationError(null);

    try {
      await loadPermissionsMap({ timeoutMs: AUTH_BOOTSTRAP_REQUEST_TIMEOUT_MS });
    } catch (error) {
      if (authBootstrapRunRef.current === runId) {
        const message = isTimeoutOrNetworkError(error)
          ? 'The server is slow to respond. You can keep browsing public pages or try signing in again.'
          : 'Unable to load permissions. Please refresh and try again.';
        setPermissions(null);
        setAnonymousState();
        setAuthInitializationError(message);
        setIsAuthInitializing(false);
        if (showStartupErrors) showErrorRef.current?.(message);
      }
      return;
    }

    try {
      await refreshAccessToken({ force: true, timeoutMs: AUTH_BOOTSTRAP_REQUEST_TIMEOUT_MS });
      if (authBootstrapRunRef.current === runId) {
        setAuthInitializationError(null);
      }
    } catch (error) {
      if (authBootstrapRunRef.current === runId) {
        setAnonymousState();
        setAuthInitializationError(
          isTimeoutOrNetworkError(error)
            ? 'The server is slow to respond. Your session could not be restored automatically.'
            : null,
        );
      }
    } finally {
      if (authBootstrapRunRef.current === runId) {
        setIsAuthInitializing(false);
      }
    }
  }, [loadPermissionsMap, refreshAccessToken, setAnonymousState]);

  const retryAuthBootstrap = useCallback(() => {
    bootstrapAuth({ showStartupErrors: true });
  }, [bootstrapAuth]);

  useEffect(() => {
    localStorage.removeItem('token');

    bootstrapAuth();

    return () => {
      authBootstrapRunRef.current += 1;
    };
  }, [bootstrapAuth]);

  useEffect(() => {
    if (!user) return undefined;

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];
    const onActivity = () => recordUserInteraction();
    events.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [recordUserInteraction, user]);

  useEffect(() => {
    if (!user) return undefined;

    const intervalId = window.setInterval(() => {
      const idleMs = Date.now() - lastInteractionRef.current;
      const countdownMs = Math.max(0, IDLE_TIMEOUT_MS - idleMs);

      if (idleMs >= IDLE_TIMEOUT_MS) {
        handleSessionExpiration('Your session expired due to inactivity. Please log in again.');
        return;
      }

      if (idleMs >= WARNING_AT_MS) {
        setSessionWarningVisible(true);
        setSessionWarningCountdownMs(countdownMs);
      } else {
        setSessionWarningVisible(false);
        setSessionWarningCountdownMs(0);
      }

      const accessExpiresAt = Date.parse(sessionMetaRef.current?.accessExpiresAt || '');
      if (
        Number.isFinite(accessExpiresAt)
        && accessExpiresAt - Date.now() <= PROACTIVE_REFRESH_WINDOW_MS
        && hasRecentInteraction()
      ) {
        refreshAccessToken({ force: false }).catch(() => {
          handleSessionExpiration();
        });
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [handleSessionExpiration, hasRecentInteraction, refreshAccessToken, user]);

  const login = useCallback(async (credentials) => {
    try {
      if (!permissionsMapRef.current) {
        await loadPermissionsMap();
      }
      const response = await axios.post(`${API_URL}/auth/login`, credentials, {
        withCredentials: true,
      });
      applyAuthenticatedState(response.data);
      return response.data;
    } catch (error) {
      showErrorRef.current?.('Login failed. Please check your credentials and try again.');
      throw error;
    }
  }, [applyAuthenticatedState, loadPermissionsMap]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const staySignedIn = useCallback(async () => {
    recordUserInteraction();
    try {
      await refreshAccessToken({ force: true });
      setSessionWarningVisible(false);
      setSessionWarningCountdownMs(0);
    } catch (error) {
      handleSessionExpiration();
    }
  }, [handleSessionExpiration, recordUserInteraction, refreshAccessToken]);

  const isAuthenticated = useCallback(() => Boolean(user && accessTokenRef.current), [user]);
  const getAccessToken = useCallback(() => accessTokenRef.current, []);
  const refreshAccessTokenForContext = useCallback((options) => refreshAccessToken(options), [refreshAccessToken]);

  const contextValue = useMemo(() => ({
    user,
    permissions,
    login,
    logout,
    isAuthenticated,
    sessionExpired,
    clearSessionExpired,
    markSessionExpired: handleSessionExpiration,
    refreshAccessToken: refreshAccessTokenForContext,
    getAccessToken,
    isAuthInitializing,
    authInitializationError,
    retryAuthBootstrap,
    tokenVersion,
    sessionWarningVisible,
    sessionWarningCountdownSeconds: Math.max(0, Math.ceil(sessionWarningCountdownMs / 1000)),
    staySignedIn,
  }), [
    clearSessionExpired,
    getAccessToken,
    handleSessionExpiration,
    isAuthenticated,
    isAuthInitializing,
    login,
    logout,
    permissions,
    refreshAccessTokenForContext,
    retryAuthBootstrap,
    authInitializationError,
    sessionExpired,
    sessionWarningCountdownMs,
    sessionWarningVisible,
    staySignedIn,
    tokenVersion,
    user,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
